import React from 'react';
import get from 'lodash.get';
import find from 'lodash.find';
import merge from 'lodash.merge';
import last from 'lodash/last';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { nanoid } from 'nanoid';
import { Link, withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import MUIDataTable from 'mui-datatables';
import UILink from '@mui/material/Link';
import { Typography, IconButton, Paper } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { Add } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import CustomToolbar from './custom-toolbar';
import {
  GET_MARKET_REPAIR,
} from '../../lib/routes/routeHelper';
import {
  FieldReportStatuses, UNASSIGNED, ASSIGNED,
  inspectTaskReasonNames, TaskStatuses, TaskTypes,
  REGULAR_PRIORITY, shouldFetchList, isOnMobile,
  RepairTicketStatuses, NEW_FIELD_REPORT_COLOR,
  timeZoneHint, ManualPickupTaskReasons,
  pickupTaskReasonNames, automatedPickupTaskReasonNames,
  sortByIntegerString, localeTimeFormat, localeDateFormat,
  CustomTooltip,
} from '../../lib/utils';
import { NOT_DIAGNOSED_ID } from '../../lib/repair-ticket-categories';
import {
  shouldExcludeDate,
  filterChipText,
} from '../../lib/table-utils';

import StatusPill from '../common/status-pill';

import RepairTicketEditModal from './repair-ticket-edit-modal';
import FieldReportEditModal from './fieldReport-edit-modal';
import Spinner from '../common/spinner';
import withPersistentSettings from '../common/table-with-persistent-settings';
import DateRangeFilter from '../common/date-range-filter';
import BulkCloseModal from '../common/bulk-close';
import TaskFormContainer from './tasks';
import CaseLinkButton from '../common/case-link-button';
import VehicleEditForm from './vehicle-edit-form';
import marketActions from '../../actions/markets';
import repairTicketActions from '../../actions/repairTickets';
import taskActions from '../../actions/tasks';
import fieldReportActions from '../../actions/fieldReports';
import vehicleActions from '../../actions/vehicles';
import zoneActions from '../../actions/zones';
import locationActions from '../../actions/locations';
import wranglerActions from '../../actions/wranglers';
import * as snackbarActions from '../../actions/snackbar';
import CreateEditTaskModal from './tasks/create-edit-task-modal';
import ActionsDropdownMenu from './actions-dropdown-menu';

dayjs.extend(timezone);

const CREATED_AT = 'Created At';
const CLOSED_AT = 'Closed At';
const SF_CASE_NUMBER = 'External Case Number';
const CASE_CATEGORY = 'Case Category';
const VEHICLE_ID = 'Vehicle';
const STATUS = 'Status';

const HAS_REPAIR_TICKET = 'Has Repair Ticket';
const NO_REPAIR_TICKET = 'Has No Repair Ticket';

const GENERATED_CATEGORY = 'Vehicle Auto Generated';
const LA_311_CATEGORY = 'LA 311 Report';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  note: {
    whiteSpace: 'nowrap',
  },
  dropdown: {
    marginTop: '8px',
    marginBottom: '8px',
    marginRight: '33px',
  },
  dropdownMobile: {
    float: 'right',
    marginLeft: '4px',
    marginRight: '4px',
    minWidth: '40px',
  },
  lastUpdateMobile: {
    float: 'right',
    marginTop: '8px',
    marginLeft: '4px',
  },
};

class FieldReportTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFieldReportFormOpen: false,
      isRepairTicketFormOpen: false,
      editFieldReport: {},
      editRepairTicket: {},
      isBulkCloseFormOpen: false,
      editing: false,
      selectedRows: [],
      editingTask: false,
      editVehicle: {},
      showEditVehicleForm: false,
      isBulkTaskFormOpen: false,
      selectedCaseIds: [],
      createRepairTickets: false,
      lastUpdateTime: new Date(),
      bulkCreateLoading: false,
    };
    this.interval = React.createRef();

    this.handleViewFieldReport = this.handleViewFieldReport.bind(this);
    this.handleCloseFieldReportForm = this.handleCloseFieldReportForm.bind(this);
    this.handleCloseRepairTicketForm = this.handleCloseRepairTicketForm.bind(this);
    this.matchingFieldReport = this.matchingFieldReport.bind(this);
    this.handleAddTask = this.handleAddTask.bind(this);
    this.handleAddRepairTicket = this.handleAddRepairTicket.bind(this);
    this.handleSaveRepairTicketForm = this.handleSaveRepairTicketForm.bind(this);
    this.handleSaveTaskForm = this.handleSaveTaskForm.bind(this);
    this.handleSaveForm = this.handleSaveForm.bind(this);
    this.handleCloseBulkClose = this.handleCloseBulkClose.bind(this);
    this.handleSaveBulkClose = this.handleSaveBulkClose.bind(this);
    this.saveTicketAsViewed = this.saveTicketAsViewed.bind(this);
    this.openVehicleForm = this.openVehicleForm.bind(this);
    this.handleCloseVehicleForm = this.handleCloseVehicleForm.bind(this);
    this.handleSaveVehicleForm = this.handleSaveVehicleForm.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.handleBulkPickupSave = this.handleBulkPickupSave.bind(this);
    this.getFieldReports = this.getFieldReports.bind(this);
  }

  componentDidMount() {
    const {
      match, marketWranglers, zones, locations, vehicles, tasks,
      fetchWranglers, fetchTasks, fetchZones, fetchVehicles, fetchLocations,
    } = this.props;
    const marketId = get(match, 'params.marketId');

    if (marketId) {
      // always fetch reports to prevent known issue that will
      // show no reports if prior market had no reports.
      this.getFieldReports();
      if (shouldFetchList(marketId, marketWranglers)) fetchWranglers({ market_id: marketId });
      if (shouldFetchList(marketId, tasks)) fetchTasks({ market_id: marketId });
      if (shouldFetchList(marketId, zones)) fetchZones({ market_id: marketId });
      if (shouldFetchList(marketId, locations)) fetchLocations({ market_id: marketId });
      if (shouldFetchList(marketId, vehicles)) fetchVehicles({ market_id: marketId });

      this.setState({ lastUpdateTime: new Date() });
    }
  }

  get getSelectedCases() {
    const { fieldReports } = this.props;
    const { selectedCaseIds } = this.state;

    return fieldReports.filter((report) => includes(selectedCaseIds, report._id));
  }

  get tableOptions() {
    const basicOptions = this.props.tableOptions;
    const { fieldReports } = this.props;
    const { selectedRows } = this.state;

    basicOptions.selectableRows = 'multiple';
    basicOptions.rowsSelected = selectedRows;

    basicOptions.onRowClick = (rowData) => {
      this.handleViewFieldReport(rowData[0]);
    };

    basicOptions.setRowProps = (row) => {
      const rowProps = {};
      const isNew = last(row);
      if (isNew) {
        rowProps.style = { backgroundColor: NEW_FIELD_REPORT_COLOR };
      }
      return rowProps;
    };

    basicOptions.customToolbar = () => (
      <>
        {isOnMobile() ? (
          <>
            <CustomToolbar onClick={this.getFieldReports} type="Refresh" />
            <Typography variant="caption" style={styles.lastUpdateMobile}>
              Last updated at:
              {' '}
              {this.state.lastUpdateTime.toLocaleString()}
            </Typography>
            <div style={styles.dropdownMobile}>
              <ActionsDropdownMenu
                onChange={this.handleDropdownChange}
                options={[
                  'Create Pickup Task and Close Case',
                  'Create Repair Ticket, Pickup Task and Close Case',
                  'Close Case',
                ]}
              />
            </div>
          </>
        ) : (
          <>
            <Typography variant="caption">
              Last updated at:
              {' '}
              {this.state.lastUpdateTime.toLocaleString()}
            </Typography>
            <CustomToolbar onClick={this.getFieldReports} type="Refresh" />
            <div style={{ float: 'right' }}>
              <ActionsDropdownMenu
                onChange={this.handleDropdownChange}
                options={[
                  'Create Pickup Task and Close Case',
                  'Create Repair Ticket, Pickup Task and Close Case',
                  'Close Case',
                ]}
              />
            </div>
          </>
        )}
      </>
    );

    basicOptions.onRowSelectionChange = (row, allSelected) => {
      const selectedIndices = allSelected.map((item) => item.dataIndex);
      const selectedCases = filter(
        fieldReports,
        (report, index) => includes(selectedIndices, index),
      );
      const selectedCaseIds = selectedCases.map((report) => report._id);
      this.setState({ selectedCaseIds, selectedRows: selectedIndices });
    };

    basicOptions.customToolbarSelect = () => (
      <div style={styles.dropdown}>
        <ActionsDropdownMenu
          onChange={this.handleDropdownChange}
          options={[
            'Create Pickup Task and Close Case',
            'Create Repair Ticket, Pickup Task and Close Case',
            'Close Case',
          ]}
        />
      </div>
    );

    return basicOptions;
  }

  get columns() {
    const { fetchOneTask } = this.props;
    const {
      selectedFilters, primarySort, fieldReports, match, caseUrls, selectedMarket,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    const tz = get(selectedMarket, 'tz', 'America/New_York');

    // the order of objects in columnData needs to match
    // the order of objects in data - both are referenced
    // on the render of the MUI datatable
    // columnData[0] is hidden from view, but contains the fieldReportId
    // and is used to allow the onRowClick above to know what FieldReport
    // it is referencing
    const columnData = [
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: CREATED_AT,
        options: {
          customBodyRender: (value) => {
            if (value) {
              return (
                <>
                  <Typography noWrap>
                    {moment.tz(value, tz).format(localeDateFormat)}
                  </Typography>
                  <Typography>{dayjs(value).tz(tz).format(localeTimeFormat)}</Typography>
                </>
              );
            } else {
              return '';
            }
          },
          filterType: 'custom',
          customFilterListOptions: {
            render: (dates) => filterChipText(dates, CREATED_AT),
          },
          filterOptions: {
            logic: (date, filters) => shouldExcludeDate(date, filters),
            display: (filterList, onChange, index, column) => (
              <DateRangeFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                name={CREATED_AT}
              />
            ),
          },
          hint: timeZoneHint(tz),
        },
      },
      {
        name: CLOSED_AT,
        options: {
          customBodyRender: (value) => {
            if (value) {
              return (
                <>
                  <Typography noWrap>
                    {moment.tz(value, tz).format(localeDateFormat)}
                  </Typography>
                  <Typography>{dayjs(value).tz(tz).format(localeTimeFormat)}</Typography>
                </>
              );
            } else {
              return '';
            }
          },
          filterType: 'custom',
          customFilterListOptions: {
            render: (dates) => filterChipText(dates, CLOSED_AT),
          },
          filterOptions: {
            logic: (date, filters) => shouldExcludeDate(date, filters),
            display: (filterList, onChange, index, column) => (
              <DateRangeFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                name={CLOSED_AT}
              />
            ),
          },
          hint: timeZoneHint(tz),
        },
      },
      {
        name: STATUS,
        options: {
          customBodyRender: (status) => {
            if (status) {
              return (
                <StatusPill
                  status={status}
                />
              );
            } else return '';
          },
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: 'External Ticket ID',
        options: {
          customBodyRender: (caseId) => {
            const matchingFieldReport = fieldReports.find(
              (report) => report.caseId === caseId,
            );
            if (matchingFieldReport) {
              if (matchingFieldReport.category === GENERATED_CATEGORY
                  || matchingFieldReport.category === LA_311_CATEGORY) {
                return (
                  <>
                    <Typography noWrap align="left" variant="string">
                      {get(matchingFieldReport, 'caseId', '')}
                      <IconButton
                        color="inherit"
                        aria-label="Copy External Ticket ID"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigator.clipboard.writeText(get(matchingFieldReport, 'caseId', ''));
                        }}
                        sx={{ ml: 0.25, pl: 0.25 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Typography>
                  </>
                );
              } else {
                return (
                  <>
                    <CaseLinkButton
                      caseUrls={caseUrls}
                      fieldReport={matchingFieldReport}
                      asLink
                    />
                    <IconButton
                      color="inherit"
                      aria-label="Copy External Ticket ID"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigator.clipboard.writeText(get(matchingFieldReport, 'caseId', ''));
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </>
                );
              }
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${SF_CASE_NUMBER}: ${v}`,
          },
        },
      },
      {
        name: CASE_CATEGORY,
        options: {
          customBodyRender: (category) => {
            if (category) {
              let catString = category;
              if (catString.length > 30) {
                catString = catString.slice(0, 30);
                catString += '...';
              }
              return (
                <Tooltip title={category}>
                  <Typography style={styles.note}>{catString}</Typography>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${CASE_CATEGORY}: ${v}`,
          },
        },
      },
      {
        name: 'Case Text',
        options: {
          sort: false,
          filter: false,
          customBodyRender: (note) => {
            if (note) {
              let noteString = note;
              if (note.length > 30) {
                noteString = note.slice(0, 30);
                noteString += '...';
              }
              return (
                <CustomTooltip
                  title={(
                    <>
                      <Typography>{note}</Typography>
                    </>
                  )}
                >
                  <Typography style={styles.note}>{noteString}</Typography>
                </CustomTooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: VEHICLE_ID,
        options: {
          customBodyRender: (value) => {
            const report = fieldReports.find(
              (fieldReport) => value === get(fieldReport, 'vehicle.humanId'),
            );
            if (value && report) {
              return (
                <Tooltip title="See Vehicle History">
                  <UILink
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.openVehicleForm(report.vehicle);
                    }}
                  >
                    {value}
                  </UILink>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${VEHICLE_ID}: ${v}`,
          },
        },
      },
      {
        name: 'Pickup Task',
        options: {
          customBodyRender: (value, tableMeta) => {
            const matchingFieldReport = this.matchingFieldReport(tableMeta.rowData[0]);
            if (value === ASSIGNED) {
              return (
                <Tooltip title="Edit Task">
                  <IconButton onClick={(event) => {
                    event.stopPropagation();
                    this.setState({ editingTask: true }, () => {
                      fetchOneTask(get(matchingFieldReport, 'task._id'));
                    });
                  }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              );
            } else if (value === UNASSIGNED) {
              return (
                <Tooltip title="Request Pickup">
                  <IconButton onClick={(event) => this.handleAddTask(event, matchingFieldReport)}>
                    <Add />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return value;
            }
          },
        },
      },
      {
        name: 'Repair Ticket',
        options: {
          hint: 'Creating a repair ticket will also create a pickup task',
          customBodyRender: (value, tableMeta) => {
            const matchingFieldReport = this.matchingFieldReport(tableMeta.rowData[0]);
            const repairTicketHumanId = get(matchingFieldReport, 'repairTicket.humanId', '');

            if (value === HAS_REPAIR_TICKET) {
              return (
                <Tooltip title="View Repair Ticket">
                  <UILink
                    component={Link}
                    to={GET_MARKET_REPAIR(marketId, repairTicketHumanId)}
                  >
                    {repairTicketHumanId}
                  </UILink>
                </Tooltip>
              );
            } else if (value === NO_REPAIR_TICKET) {
              return (
                <Tooltip title="Create repair ticket and pickup task">
                  <IconButton
                    onClick={(event) => this.handleAddRepairTicket(event, matchingFieldReport)}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return value;
            }
          },
        },
      },
      {
        name: 'isNew',
        options: {
          display: 'excluded',
        },
      },
    ];

    return this.props.addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const { fieldReports = [] } = this.props;
    const data = fieldReports.map((fieldReport) => {
      const tableData = [
        get(fieldReport, '_id', ''),
        get(fieldReport, 'createdAt') || get(fieldReport, 'caseCreationDate', ''),
        get(fieldReport, 'closedAt', ''),
        get(fieldReport, 'status', ''),
        get(fieldReport, 'caseId', ''),
        get(fieldReport, 'category', ''),
        get(fieldReport, 'note', ''),
        get(fieldReport, 'vehicle.humanId', ''),
        fieldReport.taskId ? ASSIGNED : UNASSIGNED,
        fieldReport.repairTicketId ? HAS_REPAIR_TICKET : NO_REPAIR_TICKET,
        fieldReport.status === FieldReportStatuses.NEW,
      ];
      return tableData;
    });
    return data;
  }

  getFieldReports() {
    clearInterval(this.interval.current);
    const { marketId } = get(this, 'props.match.params');
    const { fetchFieldReports } = this.props;
    fetchFieldReports({ market_id: marketId });
    this.setState({ lastUpdateTime: new Date() });
    this.interval.current = setInterval(this.getFieldReports, 300000); // 5 minutes
  }

  openVehicleForm(vehicle) {
    this.setState({
      showEditVehicleForm: true,
      editVehicle: vehicle,
    });
  }

  handleCloseVehicleForm() {
    this.setState({
      showEditVehicleForm: false,
      editVehicle: {},
    });
  }

  handleSaveVehicleForm(vehicle) {
    const {
      match,
      updateVehicle,
      fetchVehicles,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const stateUpdate = {
      showEditVehicleForm: false,
      editVehicle: {},
    };
    updateVehicle(vehicle, vehicle._id)
      .then(() => {
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Successfully updated vehicle ${vehicle.humanId}`);
        });
      })
      .then(() => fetchVehicles(query))
      .catch((error) => {
        this.setState(stateUpdate, () => {
          const errorResponseMsg = get(error, 'message') || error.toString();
          showErrorSnackbar(`Error updating vehicle ${vehicle.humanId}: ${errorResponseMsg}`);
        });
      });
  }

  handleSaveForm(fieldReport, editing, keepModalOpen) {
    const {
      showErrorSnackbar,
      showSuccessSnackbar,
      updateFieldReport,
    } = this.props;

    if (editing) {
      return updateFieldReport(fieldReport, fieldReport._id)
        .then((data) => {
          if (data.status < 300) {
            if (!keepModalOpen) {
              const stateUpdate = {
                isFieldReportFormOpen: false,
                editFieldReport: {},
                editing: false,
              };
              this.setState(stateUpdate, () => {
                showSuccessSnackbar(`Successfully updated case: ${fieldReport.caseId || fieldReport.category}`);
                this.getFieldReports();
              });
            } else {
              showSuccessSnackbar(`Successfully updated case: ${fieldReport.caseId || fieldReport.category}`);
            }
          } else {
            showErrorSnackbar('Unexpected error saving case');
          }
          return true;
        })
        .catch((error) => {
          const errMsg = get(error, 'response.data.message', '');
          showErrorSnackbar(`Error from server: ${errMsg}`);
          return false;
        });
    }
  }

  handleSaveBulkClose() {
    const {
      bulkUpdateFieldReportStatus,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const { selectedCaseIds } = this.state;

    bulkUpdateFieldReportStatus(selectedCaseIds)
      .then(() => {
        const stateUpdate = {
          isBulkCloseFormOpen: false,
          selectedRows: [],
          selectedCaseIds: [],
        };
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Successfully closed ${selectedCaseIds.length} cases.`);
        });
      })
      .then(() => this.getFieldReports())
      .catch((error) => {
        const errMsg = get(error, 'response.data.message', '');
        showErrorSnackbar(`Error from server: ${errMsg}`);
      });
  }

  handleCloseBulkClose() {
    this.setState({ isBulkCloseFormOpen: false });
  }

  handleBulkPickupSave(taskData) {
    const {
      showErrorSnackbar,
      showSuccessSnackbar,
      createTasksForCases,
      createTicketsForCases,
      updateFieldReport,
      fetchTasks,
    } = this.props;
    const { createRepairTickets } = this.state;
    const { pickupReason } = taskData;

    if (!pickupReason) return showErrorSnackbar('task reason is required');

    const { marketId } = get(this, 'props.match.params');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const selectedCases = this.getSelectedCases;
    const caseData = selectedCases.map((row) => (
      { vehicleId: row.vehicleId, detail: row.note }
    ));

    const emptyPromise = async () => {};
    const firstFunction = createRepairTickets ? createTicketsForCases : emptyPromise;

    this.setState({ bulkCreateLoading: true });
    firstFunction(caseData, marketId)
      .then((data) => {
        const { newTickets } = get(data, 'data', { newTickets: [] });
        for (const ticket of newTickets) {
          const matchingCase = selectedCases.find((c) => c.note === ticket.description
            && ticket.vehicleId === c.vehicleId);
          const updatedCase = {
            ...matchingCase,
            repairTicketId: ticket._id,
          };
          updateFieldReport(updatedCase, updatedCase._id);
        }
        return createTasksForCases(taskData, caseData);
      })
      .then((data) => {
        const { tasks, fails } = get(data, 'data', {});
        this.setState({ isBulkTaskFormOpen: false });
        for (const task of tasks) {
          const matchingCase = selectedCases.find((c) => c.note === task.note
            && task.assignedVehicleIds.includes(c.vehicleId));
          const is311Case = get(matchingCase, 'la311.createdBy311', false);
          const updatedCase = {
            ...matchingCase,
            taskId: task._id,
          };
          if (!is311Case) {
            updatedCase.status = FieldReportStatuses.CLOSED;
          }
          updateFieldReport(updatedCase, updatedCase._id);
        }

        const stateUpdate = {
          isBulkCloseFormOpen: false,
          selectedRows: [],
          selectedCaseIds: [],
          createRepairTickets: false,
          bulkCreateLoading: false,
        };
        this.setState(stateUpdate, () => {
          const numberSkipped = caseData.length - (fails.length + tasks.length);
          let snackbarText = `Successfully created tasks
            ${createRepairTickets ? ' and repair tickets ' : ''} for ${tasks.length} scooters.
            ${fails.length > 0 ? ` ${fails.length} scooters already had a pickup task.` : ''}`;
          if (numberSkipped > 0) {
            snackbarText += `Unable to create tasks
              ${createRepairTickets ? ' and repair tickets ' : ''} for ${numberSkipped} scooters.
              The case might not have a vehicle shortcode. Open the case modal for more details.`;
            showErrorSnackbar(snackbarText);
          } else {
            showSuccessSnackbar(snackbarText);
          }
        });
      })
      .then(() => fetchTasks(query))
      .then(() => this.getFieldReports())
      .catch((error) => {
        const errMsg = get(error, 'response.data.message', '');
        showErrorSnackbar(`Error from server: ${errMsg}`);
      });
  }

  matchingFieldReport(data) {
    const { fieldReports } = this.props;
    const matchingFieldReport = find(fieldReports, (fieldReport) => fieldReport._id === data);
    if (matchingFieldReport) {
      return matchingFieldReport;
    }
  }

  handleCloseFieldReportForm() {
    this.setState({ isFieldReportFormOpen: false, editing: false, editFieldReport: {} });
  }

  saveTicketAsViewed(matchingFieldReport) {
    if (matchingFieldReport.status === FieldReportStatuses.NEW) {
      // eslint-disable-next-line no-param-reassign
      matchingFieldReport.status = FieldReportStatuses.OPEN;
      // if NEW, viewing changes to OPEN
      this.handleSaveForm(matchingFieldReport, true, true);
      // second argument: editing = true
      // third arguement: keepModalOpen = true, don't close modal after save
    }
  }

  handleViewFieldReport(data) {
    const matchingFieldReport = this.matchingFieldReport(data);
    const stateUpdate = {
      editFieldReport: matchingFieldReport,
      isFieldReportFormOpen: true,
      editing: true,
    };
    this.setState(stateUpdate, () => {
      if (matchingFieldReport.status === FieldReportStatuses.NEW) {
        this.saveTicketAsViewed(matchingFieldReport);
      }
    });
  }

  handleAddRepairTicket(event, fieldReport) {
    // stopProp needed because there is also an onRowClick listener
    event.stopPropagation();
    const marketId = get(fieldReport, 'marketId');
    const vehicleId = get(fieldReport, 'vehicleId');
    const stateUpdate = {
      editRepairTicket: {
        _id: nanoid(24),
        marketId,
        status: RepairTicketStatuses.OPEN,
        categoryId: NOT_DIAGNOSED_ID,
        vehicleId,
        vehicle: {
          humanId: get(fieldReport, 'vehicle.humanId', ''),
        },
        description: get(fieldReport, 'note'),
      },
      editFieldReport: fieldReport,
      isRepairTicketFormOpen: true,
    };

    this.setState(stateUpdate, () => {
      if (fieldReport.status === FieldReportStatuses.NEW) {
        this.saveTicketAsViewed(fieldReport);
      }
    });
  }

  handleAddTask(event, fieldReport) {
    const { setOneTask } = this.props;
    // stopProp needed because there is also an onRowClick listener
    event.stopPropagation();
    const marketId = get(fieldReport, 'marketId');
    const vehicleId = get(fieldReport, 'vehicleId');
    const newTask = {
      status: TaskStatuses.ACTIVE,
      priority: REGULAR_PRIORITY,
      marketId,
      task: TaskTypes.PICK_UP,
      note: fieldReport.note || '',
      assignedVehicleIds: vehicleId ? [vehicleId] : [],
    };
    setOneTask(newTask);

    const stateUpdate = {
      editFieldReport: fieldReport,
      editingTask: false,
    };

    this.setState(stateUpdate, () => {
      if (fieldReport.status === FieldReportStatuses.NEW) {
        this.saveTicketAsViewed(fieldReport);
      }
    });
  }

  handleCloseRepairTicketForm() {
    this.setState({ isRepairTicketFormOpen: false, editRepairTicket: {} });
  }

  handleSaveRepairTicketForm(repairTicket) {
    const {
      showSuccessSnackbar,
      showErrorSnackbar,
      fetchRepairTickets,
      updateFieldReport,
      createRepairTicket,
      createTask,
      fetchTasks,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }
    let fieldReportAddOns = {
      repairTicketId: repairTicket._id,
      status: FieldReportStatuses.CLOSED,
    };
    const fieldReport = get(this, 'state.editFieldReport');
    const existingTask = get(fieldReport, 'taskId');
    if (!existingTask) {
      const vehicleId = get(fieldReport, 'vehicleId');
      const newTask = {
        _id: nanoid(24),
        status: TaskStatuses.ACTIVE,
        priority: REGULAR_PRIORITY,
        marketId,
        task: TaskTypes.PICK_UP,
        note: fieldReport.note || '',
        assignedVehicleIds: vehicleId ? [vehicleId] : [],
        pickupReason: ManualPickupTaskReasons.USER_REPORT,
      };
      fieldReportAddOns = {
        ...fieldReportAddOns,
        taskId: newTask._id,
      };
      createTask(newTask)
        .then(() => fetchTasks(query))
        .catch((e) => showErrorSnackbar('Error saving pickup task', e));
    }
    const finalUpdateFieldReport = merge(fieldReport, fieldReportAddOns);
    createRepairTicket(repairTicket)
      .then(() => {
        const stateUpdate = {
          isRepairTicketFormOpen: false,
          editRepairTicket: {},
        };

        this.setState(stateUpdate, () => {
          showSuccessSnackbar('Successfully created repair ticket and pickup task');
        });
      })
      .then(() => {
        updateFieldReport(finalUpdateFieldReport, finalUpdateFieldReport._id)
          .catch((caseErr) => {
            showSuccessSnackbar(`Successfully created repair ticket, but case remains open: ${get(caseErr, 'response.data.message')}`);
            merge(fieldReport, { status: FieldReportStatuses.OPEN });
          });
      })
      .then(() => fetchRepairTickets(query))
      .then(() => this.getFieldReports())
      .catch((error) => {
        const errMsg = get(error, 'response.data.message', '');
        showErrorSnackbar(`Error from server: ${errMsg}`);
      });
  }

  handleSaveTaskForm(task, editing) {
    const {
      showSuccessSnackbar,
      fetchTasks,
      updateFieldReport,
    } = this.props;

    const { marketId } = get(this, 'props.match.params');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }
    const fieldReportAddOns = {
      taskId: task._id,
      status: FieldReportStatuses.CLOSED,
    };
    const fieldReport = get(this, 'state.editFieldReport');
    const finalUpdateFieldReport = {
      ...fieldReport,
      ...fieldReportAddOns,
    };
    !editing && updateFieldReport(finalUpdateFieldReport, finalUpdateFieldReport._id)
      .then(() => fetchTasks(query))
      .then(() => this.getFieldReports())
      .catch((error) => {
        showSuccessSnackbar(`Successfully created task, but case remains open: ${get(error, 'response.data.message')}`);
      });
  }

  handleDropdownChange(event) {
    const selected = event.target.getAttribute('name');
    switch (selected) {
      case 'Create Pickup Task and Close Case':
        this.setState({ isBulkTaskFormOpen: true });
        break;
      case 'Create Repair Ticket, Pickup Task and Close Case':
        this.setState({ isBulkTaskFormOpen: true, createRepairTickets: true });
        break;
      case 'Close Case':
        this.setState({ isBulkCloseFormOpen: true });
        break;
      default:
        break;
    }
  }

  render() {
    const {
      getTitleFromData,
      vehicles,
      caseUrls,
      oneTask,
      selectedMarket,
      adminUrls,
      match,
      wranglers,
    } = this.props;
    const {
      editFieldReport,
      isFieldReportFormOpen,
      editing,
      isRepairTicketFormOpen,
      editRepairTicket,
      isBulkCloseFormOpen,
      editingTask,
      editVehicle,
      showEditVehicleForm,
      isBulkTaskFormOpen,
      createRepairTickets,
      selectedCaseIds,
    } = this.state;
    const marketId = get(match, 'params.marketId');
    const displayData = this.data;
    const tz = get(selectedMarket, 'tz', 'America/New_York');
    const lockVehicleField = oneTask
      && oneTask.assignedVehicleIds
      && oneTask.assignedVehicleIds.length === 1;

    const pickupOptions = pickupTaskReasonNames.map((reason) => ({
      value: reason,
      label: reason,
      isDisabled: automatedPickupTaskReasonNames.includes(reason),
    }));
    const inspectOptions = inspectTaskReasonNames.map((reason) => ({
      value: reason,
      label: reason,
    }));
    const sortedVehicles = vehicles ? sortByIntegerString(vehicles) : [];
    const vehicleOptions = sortedVehicles.map((vehicle) => ({
      value: vehicle._id,
      label: vehicle.humanId,
    }));
    const sortedWranglers = sortBy(wranglers, (wrangler) => wrangler.firstName.toLowerCase());
    const wranglerOptions = sortedWranglers.map((wrangler) => ({
      value: wrangler._id,
      label: `${wrangler.firstName} ${wrangler.lastName}`,
    }));

    return (
      <div>
        {this.props.isLoading || this.state.bulkCreateLoading
          ? <Spinner />
          : (
            <>
              <Paper style={styles.root} sx={{ mt: 3 }}>
                <MUIDataTable
                  title={getTitleFromData('Vehicle Cases', displayData)}
                  columns={this.columns}
                  data={displayData}
                  options={this.tableOptions}
                />
                <FieldReportEditModal
                  caseUrls={caseUrls}
                  key={`fieldReport-edit-form-${editFieldReport._id}`}
                  isOpen={isFieldReportFormOpen}
                  onCancel={this.handleCloseFieldReportForm}
                  onSave={this.handleSaveForm}
                  onAddTask={this.handleAddTask}
                  onAddTicket={this.handleAddRepairTicket}
                  fieldReport={editFieldReport}
                  editing={editing}
                  vehicles={vehicles}
                  tz={tz}
                />
                {oneTask && (
                  <TaskFormContainer
                    editing={editingTask}
                    task={oneTask}
                    handleExtraSave={(taskData) => this.handleSaveTaskForm(taskData)}
                    lockVehicleField={lockVehicleField}
                  />
                )}
                {isRepairTicketFormOpen && (
                  <RepairTicketEditModal
                    key={`repair-ticket-edit-form-${editRepairTicket._id}`}
                    isOpen={isRepairTicketFormOpen}
                    onCancel={this.handleCloseRepairTicketForm}
                    onSave={this.handleSaveRepairTicketForm}
                    repairTicket={editRepairTicket}
                    vehicles={vehicles}
                    tz={tz}
                  />
                )}
                <BulkCloseModal
                  message="Are you sure you want to close all these field reports?"
                  open={isBulkCloseFormOpen}
                  handleSave={this.handleSaveBulkClose}
                  handleClose={this.handleCloseBulkClose}
                />
                <VehicleEditForm
                  key={`vehicle-edit-form-${editVehicle._id}`}
                  isOpen={showEditVehicleForm}
                  onCancel={this.handleCloseVehicleForm}
                  onSave={this.handleSaveVehicleForm}
                  vehicle={editVehicle}
                  adminUrls={adminUrls}
                  showHistory
                />
                <CreateEditTaskModal
                  vehicles={sortedVehicles}
                  wranglers={wranglers}
                  open={isBulkTaskFormOpen}
                  marketId={marketId}
                  pickupOptions={pickupOptions}
                  inspectOptions={inspectOptions}
                  vehicleOptions={vehicleOptions}
                  wranglerOptions={wranglerOptions}
                  task={{ task: TaskTypes.PICK_UP, marketId }}
                  bulk
                  lockVehicleField
                  disableDetailsNote={`The pickup details will be automatically filled in base on the case text. ${createRepairTickets
                    ? '\nRepair tickets will be automatically created and the details filled in based on the case text.' : ''}`}
                  handleCloseModal={() => this.setState({
                    isBulkTaskFormOpen: false,
                    createRepairTickets: false,
                  })}
                  handleSave={(taskData) => this.handleBulkPickupSave(taskData)}
                  selectedCaseIds={selectedCaseIds}
                />
              </Paper>
            </>
          )}
      </div>
    );
  }
}

const mapStateToProps = ({
  markets, fieldReports, vehicles, settings, tasks, wranglers,
}) => ({
  isLoading: fieldReports.isLoading || vehicles.isLoading,
  selectedMarket: markets.selectedMarket,
  fieldReports: fieldReports.marketFieldReports,
  vehicles: vehicles.marketVehicles,
  caseUrls: settings.caseUrls,
  oneTask: tasks.oneTask,
  adminUrls: settings.adminUrls,
  wranglers: wranglers.marketWranglers,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...taskActions,
  ...vehicleActions,
  ...wranglerActions,
  ...zoneActions,
  ...locationActions,
  ...fieldReportActions,
  ...snackbarActions,
  ...repairTicketActions,
}, dispatch);

export default withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(FieldReportTable)),
  'FieldReportTable',
);
